// Types and Data for Kegan Adult Development survey
// Based on Robert Kegan's theory of adult development

import { v4 as uuidv4 } from 'uuid'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import { SurveyTypes } from '../Survey.ts'

export type KeganSurveyId = string

export interface KeganSurvey {
    id: KeganSurveyId
    surveyType: SurveyTypes.KEGAN
    userId: UserId
    hash?: string
    questions: KeganQuestionType[]
    scores: KeganScores
    dominantStage: KeganStage
    weightedAverageStage: number // 2.0 - 5.0
    confidence: number // 0-100
    isDraft?: boolean
    createdAt: Timestamp
    updatedAt?: Timestamp
    deletedAt?: Timestamp | null
}

// Kegan's 5 stages of adult development (we focus on 2-5, as Stage 1 is childhood)
export enum KeganStage {
    STAGE_2 = 'stage_2', // Instrumental Mind - Vlastné nástroje
    STAGE_3 = 'stage_3', // Socialized Mind - Socializovaná myseľ
    STAGE_4 = 'stage_4', // Self-Authoring Mind - Sebaautorská myseľ
    STAGE_5 = 'stage_5' // Self-Transforming Mind - Sebatransformujúca myseľ
}

export const KeganStageLabels: Record<KeganStage, string> = {
    [KeganStage.STAGE_2]: 'Stupeň 2: Vlastné nástroje',
    [KeganStage.STAGE_3]: 'Stupeň 3: Socializovaná myseľ',
    [KeganStage.STAGE_4]: 'Stupeň 4: Sebaautorská myseľ',
    [KeganStage.STAGE_5]: 'Stupeň 5: Sebatransformujúca myseľ'
}

export const KeganStageDescriptions: Record<KeganStage, string> = {
    [KeganStage.STAGE_2]: 'Definovaný vlastnými potrebami a záujmami. Vzťahy sú nástroje na dosiahnutie cieľov.',
    [KeganStage.STAGE_3]: 'Definovaný vzťahmi, rolami a očakávaniami iných. Identita pochádza z príslušnosti.',
    [KeganStage.STAGE_4]: 'Vlastná identita, hodnoty a presvedčenia. Schopnosť reflektovať vzťahy a roly.',
    [KeganStage.STAGE_5]: 'Schopnosť držať protiklady, vnímať limity vlastného pohľadu a transformovať sa.'
}

export interface KeganQuestionType {
    id: string
    scenario: string // The developmental scenario
    question: string // The question to answer
    options: KeganOption[]
    value?: string // Selected option ID
}

export interface KeganOption {
    id: string
    text: string
    stage: KeganStage
}

export interface KeganScores {
    [KeganStage.STAGE_2]: number
    [KeganStage.STAGE_3]: number
    [KeganStage.STAGE_4]: number
    [KeganStage.STAGE_5]: number
}

// 24 developmental scenarios - covers identity, relationships, values, meaning-making
// We'll randomly select 8 for each test
export const KEGAN_QUESTIONS: KeganQuestionType[] = [
    // Identity & Self-Definition
    {
        id: uuidv4(),
        scenario:
            'Po 10 rokoch v banke si sa rozhodol/a odísť a otvoriť si kaviareň. Tvoj najlepší priateľ Tomáš, ktorý ťa pozná 20 rokov, ti povie: "Toto je šialenstvo. Máš hypotéku, dve deti, a chceš riskovať všetko kvôli káve? Myslel som si, že si rozumnejší/ia." Jeho slová ťa zasiahnu, pretože jeho názor si vždy vážil/a.',
        question: 'Ako to vnímaš?',
        options: [
            {
                id: uuidv4(),
                text: 'Zraní ma to - ak ma nepodporí, tak to nie je skutočný priateľ',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Začnem pochybovať - možno naozaj robím chybu, keď aj on si to myslí',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Chápem jeho obavy, ale toto rozhodnutie vychádza z mojich hodnôt a vízií',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Jeho strach o bezpečnosť aj môj sen sú čiastočné pravdy - potrebujem plán integrujúci obe',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Na rodinnej oslave tvoj strýko Roman spustí debatu o imigrácii. Ty si celý život zastával/a liberálny postoj. Roman ti ale ukáže dáta a príbehy z jeho mesta, ktoré ti dajú zaváhať. Tvoja mama ťa pozoruje, čaká, že Romana "postavíš na miesto" ako vždy.',
        question: 'Ako reaguješ?',
        options: [
            {
                id: uuidv4(),
                text: 'Odseknem mu - mám svoj názor a on ma určite nepresvedčí',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Cítim tlak od mamy a rodiny - neviem, či mám súhlasiť alebo nesúhlasiť',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Zaujmú ma jeho dáta, prehodnotím svoj názor podľa vlastného úsudku',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Vidím, že môj liberálny ideál aj jeho lokálna skúsenosť zachytávajú rôzne časti reality',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    // Relationships & Conflict
    {
        id: uuidv4(),
        scenario:
            'Tvoja partnerka Zuzana ti večer povie: "Minulý mesiac si bol s kamarátmi štyrikrát, so mnou na rande ani raz. Mám pocit, že som ti menej dôležitá." Pritom vieš, že tí kamaráti ti pomáhali zvládnuť náročné obdobie v práci a Zuzana o tom nevie.',
        question: 'Ako pristupuješ k tejto situácii?',
        options: [
            { id: uuidv4(), text: 'Poviem jej, že to je môj čas a nemá mi ho kontrolovať', stage: KeganStage.STAGE_2 },
            {
                id: uuidv4(),
                text: 'Cítim vinu - nechcem, aby bola nešťastná, ale nechcem ani stratiť kamarátov',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Vysvetlím jej situáciu a spoločne nastavíme, čo potrebujeme obaja',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Jej potreba blízkosti aj moja potreba podpory sú legitímne - hľadám, ako ich integrovať',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'V práci si teamleader a dvaja tvoji kolegovia - Martin a Eva - sa dostali do ostrého konfliktu kvôli rozdeleniu úloh. Martin ťa zastaví na chodbe: "Vieš, že mám pravdu, povedz to Eve." O hodinu ťa chytí Eva: "Martinovi ide len o seba, musíš ma podporiť." Obaja sú tvoji priatelia aj mimo práce.',
        question: 'Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Podporím toho, s kým mám lepší vzťah alebo kto mi môže viac pomôcť',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Cítim sa roztrhaný/á - bojím sa, že jeden z nich sa na mňa nahnevá',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Poviem obom svoj názor založený na tom, čo považujem za správne pre tím',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Ich konflikt môže odhaliť systémový problém v rozdelení úloh - pomôžem im to spolu preskúmať',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    // Values & Principles
    {
        id: uuidv4(),
        scenario:
            'Pracuješ v marketingovej agentúre. Šéfka Petra oznámi, že firma prijala klienta - online kasíno cielené na mladých ľudí. Je to legálne, ale ty vieš, že hazard ničí životy. Všetci kolegovia mlčia alebo nadšene gratulujú k veľkej zákazke. Petra sa na teba pozrie: "Dúfam, že sa tešíš, tento klient nás udrží rok."',
        question: 'Ako sa postavíš k tejto situácii?',
        options: [
            {
                id: uuidv4(),
                text: 'Ak za to dobre zaplatia a ostatní s tým nemajú problém, nebudem vyčnievať',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Cítim sa nepríjemne, ale nechcem ísť proti tímu ani sklamať šéfku',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Poviem Petre, že mám etické výhrady a nebudem na tomto projekte pracovať',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Moje etické výhrady aj potreba firmy prežiť sú reálne - hľadám, či existuje stredná cesta',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Tvoj otec je úspešný podnikateľ, ktorý si celý život zakladá na tvrdej práci a "každý za seba". Ty si začal/a meditovať, čítať o spiritualite a veríš v spoluprácu a zdieľanie. Pri nedeľnom obede otec povie: "Tieto tvoje new-age hlúposti ťa nikam nedostanú. Kedy dospeješ?"',
        question: 'Ako to zvládaš?',
        options: [
            {
                id: uuidv4(),
                text: 'Nahnevám sa - prečo ma nemôže nechať na pokoji so svojimi názormi',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Cítim sa zranený/á - asi som ho sklamal/a a nespĺňam jeho očakávania',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Rešpektujem jeho cestu, ale idem svojou - to čo funguje pre neho, nemusí pre mňa',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Jeho dôraz na tvrdosť aj môj na spoluprácu oba reagujú na niečo reálne - obe majú limity',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    // Career & Purpose
    {
        id: uuidv4(),
        scenario:
            'Headhunter ti zavolá s ponukou: pozícia v korporáte s platom o 40% vyšším. Práca je v oblasti financií, ktorá ťa nikdy nebavila - ale splatíš hypotéku o 5 rokov skôr. Tvoja aktuálna práca v neziskovke ťa baví, ale ledva vyjdeš. Partner vraví: "Konečne niečo poriadne."',
        question: 'Ako sa rozhoduješ?',
        options: [
            {
                id: uuidv4(),
                text: 'Beriem to - peniaze vyriešia problémy, bavenie môže počkať',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Partner má pravdu a všetci by si vybrali lepší plat - asi by som mal/a tiež',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Zvážim, čo je pre mňa zmysluplná práca a či to sedí s mojimi hodnotami',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Finančná istota aj zmysluplnosť sú reálne potreby - hľadám tretiu cestu, nie "buď-alebo"',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Po 8 rokoch ako úspešný právnik si uvedomíš, že ťa práca vyčerpáva a nenaplňuje. Roky si investoval/a do kariéry, máš rešpekt kolegov, rodičia sú na teba hrdí. Ale ráno sa budíš s pocitom prázdnoty. Kamarát sa ťa spýta: "Prečo robíš niečo, čo ťa neberie?"',
        question: 'Ako na to reaguješ?',
        options: [
            {
                id: uuidv4(),
                text: 'Príliš som do toho investoval/a - vzdať sa teraz by bola hlúposť',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Čo by si pomysleli rodičia a kolegovia, keby som odišiel/a?',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Moja hodnota nie je v pozícii - ak ma to nenaplňuje, môžem zmeniť smer',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'To, kým som sa stal/a ako právnik, aj to, kým chcem byť, sú časti mňa - ako ich prepojiť?',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    // Feedback & Growth
    {
        id: uuidv4(),
        scenario:
            'Na 360° hodnotení dostaneš spätnú väzbu od tímu: "Máš skvelé nápady, ale tvoj štýl komunikácie ľudí odráza. Pôsobíš povýšenecky." Jeden kolega napísal: "Niekedy mám pocit, že si myslíš, že si lepší ako ostatní." Vždy si sa považoval/a za priameho, nie povýšeného.',
        question: 'Ako to prijímaš?',
        options: [
            {
                id: uuidv4(),
                text: 'To je ich problém - ja som len priamy/a, oni sú precitlivení',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Cítim sa hrozne - asi ma v tíme nemajú radi a sklamal/a som ich',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Zamyslím sa, či je v tom pravda a ak áno, zapracujem na komunikácii',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Môj zámer byť priamy/a aj ich vnem povýšenosti môžu byť obe pravdivé - chcem pochopiť prečo',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    // Authority & Systems
    {
        id: uuidv4(),
        scenario:
            'Tvoj nový riaditeľ Jozef mení systém hodnotenia zamestnancov. Ty si 5 rokov budoval/a férový systém, ktorý fungoval. Jozef ti povie: "Od pondelka to robíme po novom, bez diskusie." Jeho systém ti pripadá menej spravodlivý, ale on je šéf a má podporu vedenia.',
        question: 'Aká je tvoja reakcia?',
        options: [
            { id: uuidv4(), text: 'Urobím, čo povie - nemá zmysel bojovať s vedením', stage: KeganStage.STAGE_2 },
            {
                id: uuidv4(),
                text: 'Je to šéf a má podporu - asi vie, čo robí, mal/a by som poslúchnuť',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Vyjadrím svoje výhrady - ak s nimi nesúhlasím, poviem to',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Môj systém aj jeho môžu mať silné stránky - skúsim pochopiť, čo vidí on a ja nie',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    // Meaning-Making & Paradox
    {
        id: uuidv4(),
        scenario:
            'Celý život si veril/a, že tvrdá práca sa vždy vyplatí. Potom tvoj kamarát Miro, ktorý pracoval najtvrdšie zo všetkých, dostane rakovinu a stratí prácu. Medzitým jeho kolega, ktorý "šliape na peniaze", dostane povýšenie. Tvoje presvedčenie o spravodlivom svete sa rúca.',
        question: 'Ako to zvládaš?',
        options: [
            {
                id: uuidv4(),
                text: 'Miro mal asi smolu - to neznamená, že tvrdá práca nefunguje',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Som zmätený/á - neviem, čomu mám veriť, keď aj tvrdá práca nezaručí nič',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Prehodnotím svoj vzťah k práci - tvrdosť nie je jediná hodnota',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Moje presvedčenie o zásluhovosti aj realita náhody sú obe pravdivé - svet je komplexnejší',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Veríš v slobodu jednotlivca a zároveň v zodpovednosť za komunitu. Tvoja obec hlasuje o zákaze áut v centre - dobré pre životné prostredie, ale obmedzí to tvoju slobodu pohybu. Obidve hodnoty, ktoré vyznávaš, sú teraz v priamom konflikte a musíš hlasovať.',
        question: 'Ako s tým naložíš?',
        options: [
            { id: uuidv4(), text: 'Hlasujem podľa toho, čo je pre mňa praktickejšie', stage: KeganStage.STAGE_2 },
            {
                id: uuidv4(),
                text: 'Zistím, ako hlasuje väčšina, a pridám sa - nechcem vyčnievať',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Rozhodnem sa podľa toho, ktorá hodnota je pre mňa dôležitejšia',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Tento konflikt mi ukazuje, že moje hodnoty nie sú absolútne - hľadám riešenie zahŕňajúce obe',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    // More scenarios...
    {
        id: uuidv4(),
        scenario:
            'Tvoj mentor profesor Novák, ktorého si roky obdivoval/a pre jeho integritu, je obvinený z plagiátorstva. Dôkazy sú presvedčivé. Tento človek ti pomohol nájsť smer v živote, jeho knihy formovali tvoje myslenie. Teraz jeho meno je spájané s podvodom.',
        question: 'Ako to ovplyvní tvoj pohľad?',
        options: [
            { id: uuidv4(), text: 'Cítim sa zradený/á - už nikdy mu nebudem dôverovať', stage: KeganStage.STAGE_2 },
            {
                id: uuidv4(),
                text: 'Som dezorientovaný/á - ak on nie je taký, ako som si myslel/a, čomu môžem veriť?',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Uznávam, že aj múdri ľudia zlyhávajú - jeho myšlienky môžem oddeliť od jeho činu',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Moje idealizovanie ho možno bránilo vidieť ho ako celého človeka - učím sa z toho',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Tvoja rodná dedina má tradíciu poľovačiek ako spoločenské udalosti. Ty si sa stal/a vegetariánom a zabíjanie zvierat ti vadí. Otec ťa pozve na tradičnú poľovačku: "Deduško by chcel, aby si bol pri tom. Celá rodina tam bude." Odmietnutie by ranilo otca aj znevážilo pamiatku dedka.',
        question: 'Ako sa k tomu postavíš?',
        options: [
            { id: uuidv4(), text: 'Nepôjdem - je mi to proti srsti a je to moja vec', stage: KeganStage.STAGE_2 },
            {
                id: uuidv4(),
                text: 'Pôjdem, nechcem sklamať otca ani rodinu - aj keď sa mi to nepáči',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Vysvetlím otcovi svoje hodnoty a navrhnem iný spôsob, ako uctiť dedka',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Moje hodnoty aj hodnota rodinných tradícií sú reálne - hľadám, ako byť prítomný bez kompromisu oboch',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Tvoja dcéra dostala štipendium na prestížnu školu v Prahe. Musela by odísť z domu ako 14-ročná. Veríš vo vzdelanie, ale aj v to, že deti potrebujú rodinu. Manželka vraví: "Je to jej šanca." Dcéra je nadšená, ale vidíš v jej očiach aj strach.',
        question: 'Ako pristúpiš k rozhodnutiu?',
        options: [
            {
                id: uuidv4(),
                text: 'Rozhodnem podľa toho, čo je pre nás ako rodinu najpohodlnejšie',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Neviem sa rozhodnúť - čo ak urobím chybu a dcéra alebo manželka mi to vyčítajú?',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Zvážim obe hodnoty a rozhodnem sa podľa toho, čo považujem za najlepšie pre dcéru',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Toto rozhodnutie odhalí, že moje hodnoty sú v napätí - ako môžem čestne držať obe?',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Tri roky si budoval/a startup. Včera ti investor povedal, že firma nedostane ďalšie kolo financovania a do mesiaca skrachuje. Musíš prepustiť 12 ľudí, ktorým si sľúbil/a istotu. Tvoja identita bola "úspešný podnikateľ". Teraz nevieš, kto si.',
        question: 'Ako reaguješ?',
        options: [
            { id: uuidv4(), text: 'Hľadám vinníka - investor nás zradil, trh bol zlý', stage: KeganStage.STAGE_2 },
            {
                id: uuidv4(),
                text: 'Cítim sa ako zlyhanie - sklamal/a som tím, rodinu, všetkých',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Je to bolestivé, ale moja hodnota nie je v jednej firme - môžem začať znova',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Moja identita bola príliš naviazaná na úspech - toto mi ukazuje, kto som pod tým všetkým',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Celý život si riešil/a problémy analyticky - rozložíš na časti, nájdeš príčinu, opravíš. Teraz čelíš manželskej kríze. Čím viac analyzuješ, tým je to horšie. Terapeut ti povie: "Prestań riešiť a začni cítiť." Ale ty nevieš, ako inak fungovať.',
        question: 'Ako pristúpiš k situácii?',
        options: [
            { id: uuidv4(), text: 'Terapeut asi nerozumie - skúsim ešte viac analyzovať', stage: KeganStage.STAGE_2 },
            {
                id: uuidv4(),
                text: 'Neviem, čo robiť - možno mám robiť to, čo hovoria experti?',
                stage: KeganStage.STAGE_3
            },
            { id: uuidv4(), text: 'Prijmem, že potrebujem nový prístup a naučím sa ho', stage: KeganStage.STAGE_4 },
            {
                id: uuidv4(),
                text: 'Moje analytické myslenie aj potreba cítiť sú časti mňa - ako ich môžem integrovať?',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Si členom bežeckého klubu aj environmentálneho hnutia. Klub plánuje bežecké preteky v chránenom území - bude to skvelá akcia, ale môže poškodiť biotop. Environmentálni aktivisti ťa žiadajú, aby si protestoval/a. Bežci hovoria: "Si jeden z nás, podpor nás."',
        question: 'Ako sa cítiš a konáš?',
        options: [
            {
                id: uuidv4(),
                text: 'Podporím tú skupinu, kde mám lepšie vzťahy alebo väčší prospech',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Cítim sa roztrhaný/á - obidve skupiny sú moja "rodina", nechcem nikoho sklamať',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Zaujmem vlastný postoj podľa toho, čo považujem za správne, aj keď sa to niekomu nepáči',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Obe skupiny majú legitímne hodnoty - môžem pomôcť nájsť riešenie, ktoré rešpektuje oboje',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Celý život si sa definoval/a ako vynikajúci huslista. Hudba bola tvoja identita. Po autonehode ti lekári povedia, že už nikdy nebudeš môcť profesionálne hrať. Rodina vraví: "Aspoň žiješ." Ale ty si myslíš: "Kto som bez huslí?"',
        question: 'Ako to ovplyvní tvoje vnímanie seba?',
        options: [
            {
                id: uuidv4(),
                text: 'Som zničený/á - bez huslí nemám hodnotu, život stratil zmysel',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Sklamal/a som všetkých - učiteľov, rodičov, fanúšikov, ktorí do mňa verili',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Je to devastujúce, ale som viac než huslista - nájdem nový spôsob vyjadrenia',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Toto odhaľuje, ako úzko som svoju hodnotu naviazal/a na jednu vec - kto som naozaj?',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Celý život si veril/a v racionalitu a vedu. Potom ti zomrie matka a zažiješ hlbokú "duchovnú" skúsenosť - pocit jej prítomnosti, ktorý sa nedá vysvetliť. Tvoji racionálni priatelia by to považovali za blud. Ale pre teba to bolo reálnejšie než čokoľvek predtým.',
        question: 'Aký je tvoj postoj?',
        options: [
            {
                id: uuidv4(),
                text: 'Bol to asi šok a emócie - držím sa faktov, ktoré poznám',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Som zmätený/á - čo ak som sa mýlil/a celý život? Čo si o mne pomyslia?',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Prijmem túto skúsenosť ako súčasť reality, aj keď ju neviem vysvetliť vedecky',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Moja racionálna stránka aj táto skúsenosť sú obe časti mojej reality - nemusím si vybrať',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Vyrástol/a si na Slovensku a celý život si bol/a hrdý/á Slovák/Slovenka. Potom si rok žil/a v Dánsku a uvedomil/a si, že mnohé "slovenské hodnoty" sú vlastne limitujúce. Tvoji rodičia sa cítia zradení, keď kritizuješ domovinu. Ty sa cítiš medzi dvoma svetmi.',
        question: 'Ako na to reaguješ?',
        options: [
            {
                id: uuidv4(),
                text: 'Dánsko je proste lepšie - prečo by som mal/a brániť niečo horšie?',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Cítim sa previnilo - akobych zradil/a rodinu a kultúru, ktorá ma vychovala',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Môžem milovať Slovensko a zároveň vidieť, kde zaostávame - to nie je zrada',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Moja slovenská aj "svetová" identita sú časti mňa - nemusím si vybrať jednu',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Roky trápiš dlhodobú úzkosť. Skúšal/a si terapiu, lieky, meditáciu - nič nepomáhalo. Potom ti známy povie: "Skús psychedeliká, mne zmenili život." Je to nelegálne a proti všetkému, čo si doteraz veril/a. Ale si zúfalý/á.',
        question: 'Ako pristúpiš k tejto perspektíve?',
        options: [
            {
                id: uuidv4(),
                text: 'Ak mi to rýchlo nepomôže alebo je to komplikované, tak nie',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Čo by si pomysleli rodičia alebo partner, keby som skúšal/a drogy?',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Preskúmam dôkazy a rozhodnem sa podľa vlastného úsudku o rizikách a prínosoch',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Moje doterajšie presvedčenia o liečbe aj táto nová možnosť môžu byť obe čiastočne pravdivé',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Tvoja staršia sestra Katka, ktorú si vždy obdivoval/a, ti povie: "Žiješ príliš povrchne - len práca, kariéra, úspech. Kedy si naposledy urobil/a niečo pre dušu?" Jej slová ťa zasiahnu, pretože si práve dostal/a povýšenie a cítil/a si sa šťastný/á.',
        question: 'Ako to prijmeš?',
        options: [
            { id: uuidv4(), text: 'Nahnevám sa - kto jej dal právo súdiť môj život?', stage: KeganStage.STAGE_2 },
            {
                id: uuidv4(),
                text: 'Začnem pochybovať - možno naozaj žijem zle a všetci to vidia okrem mňa',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Beriem jej pohľad vážne, ale viem, čo ma napĺňa - moje hodnoty sú moje',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Jej kritika aj môj pocit naplnenia môžu byť obe pravdivé - čo mi to hovorí o mne?',
                stage: KeganStage.STAGE_5
            }
        ]
    },

    {
        id: uuidv4(),
        scenario:
            'Po 15 rokoch ako vedúci oddelenia príde nový CEO a oznámi reštrukturalizáciu. Tvoje oddelenie sa zruší, ty môžeš zostať ako radový zamestnanec. Kolegovia sa pýtajú: "Čo budeš robiť? Ty si predsa vždy bol šéf." Tvoja identita v práci sa rúca.',
        question: 'Ako na to nazeráš?',
        options: [
            {
                id: uuidv4(),
                text: 'Budem bojovať o svoju pozíciu alebo odídem - nechcem byť obyčajný zamestnanec',
                stage: KeganStage.STAGE_2
            },
            {
                id: uuidv4(),
                text: 'Ak už nebudem šéf, kým budem? Čo si o mne pomyslia kolegovia?',
                stage: KeganStage.STAGE_3
            },
            {
                id: uuidv4(),
                text: 'Moja hodnota nie je v titule - môžem prispieť aj v inej role',
                stage: KeganStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Táto situácia mi ukazuje, ako veľmi som sa identifikoval s rolou - kto som bez nej?',
                stage: KeganStage.STAGE_5
            }
        ]
    }
]

// Fisher-Yates shuffle algorithm
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const left = shuffled[i]
        const right = shuffled[j]

        if (left === undefined || right === undefined) {
            continue
        }

        shuffled[i] = right
        shuffled[j] = left
    }
    return shuffled
}

// Get random subset of questions (20 out of 24)
function getRandomQuestionSubset(): KeganQuestionType[] {
    const shuffled = shuffleArray(KEGAN_QUESTIONS)
    return shuffled.slice(0, 20)
}

// Initialize a new Kegan survey with random question subset
export function initializedKeganSurvey(userId: UserId): Omit<KeganSurvey, 'id' | 'createdAt'> {
    return {
        surveyType: SurveyTypes.KEGAN,
        userId,
        hash: uuidv4(),
        questions: getRandomQuestionSubset().map((q) => ({ ...q, value: undefined })),
        scores: {
            [KeganStage.STAGE_2]: 0,
            [KeganStage.STAGE_3]: 0,
            [KeganStage.STAGE_4]: 0,
            [KeganStage.STAGE_5]: 0
        },
        dominantStage: KeganStage.STAGE_3,
        weightedAverageStage: 3.0,
        confidence: 0,
        isDraft: true,
        deletedAt: null
    }
}
